import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Entitlements } from "@websede/contracts";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { LEGAL } from "../legal/company.js";

const DAY_MS = 86_400_000;

/** What the workspace's plan allows; shared by the banner, notes and anything else that asks. */
export function useEntitlements() {
  const session = useSession();
  return useQuery({
    queryKey: ["billing", "entitlements"],
    queryFn: () => api.billing.entitlements(),
    enabled: Boolean(session.data),
    staleTime: 60_000,
  });
}

export function trialDaysLeft(state: Pick<Entitlements, "trialEndsAt">, now = Date.now()): number {
  if (!state.trialEndsAt) return 0;
  return Math.max(0, Math.ceil((Date.parse(state.trialEndsAt) - now) / DAY_MS));
}

/**
 * A strip above every page when the plan needs attention: the trial's last week, a trial that
 * needs a card for outside mail, a failed payment, a paused workspace, or outside sending paused
 * for bounces and complaints. Administrators get the
 * way to billing; everyone else is told whom to ask.
 */
export function BillingBanner() {
  const { t } = useTranslation();
  const session = useSession();
  const entitlements = useEntitlements();
  const e = entitlements.data;
  if (!e) return null;
  const days = trialDaysLeft(e);
  let message: string | null = null;
  let urgent = false;
  if (e.sendingPaused) {
    message = t("billing.banner.sendingPaused", { support: LEGAL.supportEmail });
    urgent = true;
  } else if (e.access === "expired") {
    message = t("billing.banner.expired");
    urgent = true;
  } else if (e.access === "past_due") {
    message = t("billing.banner.pastDue");
    urgent = true;
  } else if (e.access === "trial" && !e.canSendExternal) {
    message = t("billing.banner.cardNeeded", { count: days });
  } else if (e.access === "trial" && days <= 7) {
    message = t("billing.banner.trialEnds", { count: days });
  }
  if (!message) return null;
  const role = session.data?.user.role;
  const isAdmin = role === "owner" || role === "admin";
  return (
    <div
      role="status"
      className={`flex shrink-0 items-center gap-3 border-b border-rule px-4 py-1.5 text-xs ${urgent ? "bg-danger-soft text-danger" : "bg-accent-soft text-accent"}`}
    >
      <span className="min-w-0 flex-1">{message}</span>
      {e.sendingPaused ? null : isAdmin ? (
        <Link
          to="/admin/billing"
          className="shrink-0 font-medium underline-offset-2 hover:underline"
        >
          {e.access === "past_due" ? t("billing.banner.fix") : t("billing.banner.choose")}
        </Link>
      ) : (
        <span className="shrink-0">{t("billing.banner.askAdmin")}</span>
      )}
    </div>
  );
}
